import type {BaseQueryApi} from '@reduxjs/toolkit/query';
import {isEqual} from 'lodash';
import {baseQuery} from '../../baseQuery.ts';
import {unwrapData} from '../../apiResponse.ts';
import {BACKEND_PAGE_SIZE} from '../../fetchAllPages.ts';

export const STATUSES_LONG_POLL_WAIT_SECONDS = 25;
export const STATUSES_FALLBACK_POLLING_MS = 5000;
export const STATUSES_LONG_POLL_MIN_INTERVAL_MS = 1000;
export const STATUSES_WATCH_DEBOUNCE_MS = 200;
const LONG_POLL_WAIT_HEADER = 'X-Long-Poll-Wait';

type StatusesStore = Pick<BaseQueryApi, 'dispatch' | 'getState'>;

export type StatusRecord = {userId: string | number};

export type StatusesWatch = {
    users: readonly string[];
    apply: (statuses: readonly StatusRecord[]) => void;
};

type StatusesPollResult = Awaited<ReturnType<typeof baseQuery>>;

export const mergeStatuses = <T extends StatusRecord>(incoming: readonly StatusRecord[]) => (draft: T[]) => {
    const incomingByUserId = new Map(incoming.map((status) => [String(status.userId), status]));
    draft.forEach((status, index) => {
        const next = incomingByUserId.get(String(status.userId));
        if (next && !isEqual(status, next)) {
            draft[index] = next as T;
        }
    });
};

const sleep = (ms: number, signal: AbortSignal) => new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
        clearTimeout(timer);
        resolve();
    }, {once: true});
});

const chunksOf = (users: readonly string[]): string[][] => {
    const chunks: string[][] = [];
    for (let index = 0; index < users.length; index += BACKEND_PAGE_SIZE) {
        chunks.push(users.slice(index, index + BACKEND_PAGE_SIZE));
    }
    return chunks;
};

const nextPollDelay = (result: StatusesPollResult, startedAt: number): number => {
    if (result.error) {
        return STATUSES_FALLBACK_POLLING_MS;
    }
    const serverParks = Number(result.meta?.response?.headers.get(LONG_POLL_WAIT_HEADER) ?? 0) > 0;
    const minInterval = serverParks ? STATUSES_LONG_POLL_MIN_INTERVAL_MS : STATUSES_FALLBACK_POLLING_MS;
    return Math.max(0, startedAt + minInterval - Date.now());
};

class StatusesLongPoll {
    private readonly watches = new Set<StatusesWatch>();
    private watchedUsers = '';
    private controller?: AbortController;
    private restartTimer?: ReturnType<typeof setTimeout>;

    constructor(private readonly store: StatusesStore) {}

    watch(watch: StatusesWatch): () => void {
        this.watches.add(watch);
        this.scheduleRestart();
        return () => {
            this.watches.delete(watch);
            this.scheduleRestart();
        };
    }

    private scheduleRestart() {
        clearTimeout(this.restartTimer);
        this.restartTimer = setTimeout(() => this.restart(), STATUSES_WATCH_DEBOUNCE_MS);
    }

    private restart() {
        const users = [...new Set([...this.watches].flatMap((watch) => watch.users))].sort();
        const watchedUsers = users.join(',');
        if (watchedUsers === this.watchedUsers) {
            return;
        }
        this.watchedUsers = watchedUsers;
        this.controller?.abort();
        this.controller = users.length > 0 ? new AbortController() : undefined;
        const signal = this.controller?.signal;
        if (signal) {
            chunksOf(users).forEach((chunk) => void this.poll(chunk, signal));
        }
    }

    private async poll(users: readonly string[], signal: AbortSignal) {
        while (!signal.aborted) {
            const startedAt = Date.now();
            const result = await this.request(users, signal);
            if (signal.aborted) {
                return;
            }
            if (!result.error) {
                this.apply(unwrapData(result.data as {statuses?: StatusRecord[]}).statuses ?? []);
            }
            await sleep(nextPollDelay(result, startedAt), signal);
        }
    }

    private async request(users: readonly string[], signal: AbortSignal): Promise<StatusesPollResult> {
        const api: BaseQueryApi = {
            dispatch: this.store.dispatch,
            getState: this.store.getState,
            signal,
            abort: () => undefined,
            extra: undefined,
            endpoint: 'getStatusesLongPoll',
            type: 'query',
        };
        try {
            return await baseQuery({url: 'api/v1/statuses', params: {users: users.join(','), wait: STATUSES_LONG_POLL_WAIT_SECONDS}}, api, {});
        } catch (error) {
            return {error: {status: 'CUSTOM_ERROR', error: String(error)}};
        }
    }

    private apply(statuses: readonly StatusRecord[]) {
        this.watches.forEach((watch) => watch.apply(statuses));
    }
}

const longPolls = new WeakMap<StatusesStore['dispatch'], StatusesLongPoll>();

export const watchStatuses = (store: StatusesStore, watch: StatusesWatch): (() => void) => {
    let longPoll = longPolls.get(store.dispatch);
    if (!longPoll) {
        longPoll = new StatusesLongPoll(store);
        longPolls.set(store.dispatch, longPoll);
    }
    return longPoll.watch(watch);
};
